import { useSiteContent } from "@/shared/content/SiteContentProvider"
import { openWhatsApp } from "@/shared/lib/whatsapp"
import { Button } from "@/shared/ui/Button"
import { CheckCircle, InfoCircle } from "@/shared/ui/icons"
import type { AccreditationReceipt } from "@/services/types"

/**
 * Os dois desfechos do envio.
 *
 * O de erro nao e um aviso vermelho e ponto final: ele entrega os dois canais
 * que a DIMPET ja usa. Uma clinica que preencheu quarenta campos e leu "tente
 * novamente" fecha a aba e nao volta — e o pedido de credenciamento se perde
 * por um problema que nao era dela.
 */

/** Canais de contato, repetidos nos dois desfechos de falha. */
function ContactChannels({ email }: { email: string }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3">
      <Button variant="primary" size="md" onClick={() => openWhatsApp()}>
        Falar pelo WhatsApp
      </Button>

      <Button
        variant="secondary"
        size="md"
        onClick={() => {
          window.location.href = `mailto:${email}?subject=${encodeURIComponent("Credenciamento — rede DIMPET")}`
        }}
      >
        Enviar e-mail
      </Button>
    </div>
  )
}

export interface SubmitErrorPanelProps {
  message: string
}

export function SubmitErrorPanel({ message }: SubmitErrorPanelProps) {
  const { company } = useSiteContent()

  return (
    <div
      role="alert"
      className="flex flex-col gap-4 rounded-2xl border border-dourado/40 bg-dourado/8 p-5"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0 text-dourado-escuro">
          <InfoCircle />
        </span>
        <p className="text-sm text-verde-profundo/80 leading-relaxed">{message}</p>
      </div>

      <ContactChannels email={company.email} />
    </div>
  )
}

export interface AccreditationSuccessProps {
  receipt: AccreditationReceipt
  onReset: () => void
}

export function AccreditationSuccess({ receipt, onReset }: AccreditationSuccessProps) {
  return (
    <div
      // `status` e nao `alert`: a confirmacao e informacao, nao urgencia — o
      // leitor de tela a anuncia sem interromper o que estiver lendo.
      role="status"
      className="flex flex-col items-start gap-5 rounded-2xl border border-verde-profundo/10 bg-white p-8 lg:p-10"
    >
      <span className="w-12 h-12 rounded-xl bg-verde-profundo text-off-white flex items-center justify-center">
        <CheckCircle size={22} />
      </span>

      <div className="flex flex-col gap-3">
        <h3 className="text-2xl font-medium text-verde-profundo leading-snug">Ficha recebida</h3>

        <p className="text-base text-verde-profundo/60 leading-relaxed max-w-lg">
          Sua ficha de credenciamento chegou à equipe DIMPET. A análise da documentação e o retorno acontecem
          pelo e-mail que você informou.
        </p>
      </div>

      <dl className="flex flex-wrap gap-x-10 gap-y-3">
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs font-semibold tracking-widest uppercase text-dourado-escuro">Protocolo</dt>
          <dd className="text-base font-medium text-verde-profundo">{receipt.protocol}</dd>
        </div>

        <div className="flex flex-col gap-0.5">
          <dt className="text-xs font-semibold tracking-widest uppercase text-dourado-escuro">Recebida em</dt>
          <dd className="text-base font-medium text-verde-profundo">{formatReceivedAt(receipt.receivedAt)}</dd>
        </div>
      </dl>

      <Button variant="secondary" size="md" onClick={onReset}>
        Enviar outra ficha
      </Button>
    </div>
  )
}

/** ISO -> data e hora legiveis. Devolve o original se o destino mandar outra coisa. */
function formatReceivedAt(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
}
